import { trainingZoneNumber } from './trainingZones';
/**
 * Workout step intensity helpers — mirrors Ai-Tempo intensityMetricHelpers.ts
 */

import type {
  PrimaryIntensityMetric,
  PrimaryIntensityTargetOrRange,
} from './workoutBuilderSettings';
import {
  ABSOLUTE_POWER_THRESHOLD,
  FTP_PERCENT_MAX,
  HR_PERCENT_MAX,
  formatIntensityTargetLabel,
  normalizeStoredTargetToPercent,
} from './workoutStructure';

export type { PrimaryIntensityMetric };
export {
  ABSOLUTE_POWER_THRESHOLD,
  FTP_PERCENT_MAX,
  HR_PERCENT_MAX,
  formatIntensityTargetLabel,
  normalizeStoredTargetToPercent,
};

export type IntensityMetricCategory = 'power' | 'hr' | 'speed' | 'cadence' | 'open';

export const RANGE_MODE_PERCENT = 1;
export const RANGE_MODE_ZONE = 2;
export const RANGE_MODE_ABSOLUTE = 3;

export const PRIMARY_INTENSITY_LABELS: Record<PrimaryIntensityMetric, string> = {
  percentOfFtp: '% FTP',
  percentOfMaxHr: '% Max HR',
  percentOfLTHR: '% LTHR',
  percentOfThresholdSpeed: '% PACE',
};

export const RANGE_TOGGLE_LABELS: Record<
  'power' | 'hr' | 'speed',
  { percent: string; zone: string; absolute: string }
> = {
  power: { percent: '% FTP', zone: 'Power Zone', absolute: 'W' },
  hr: { percent: '% LTHR', zone: 'HrZone', absolute: 'bpm' },
  speed: { percent: '% PACE', zone: 'speedzone', absolute: 'Pace' },
};

export type IntensityThresholds = {
  ftp?: number;
  ltHr?: number;
  maxHr?: number;
  thresholdSpeedMps?: number;
};

/** True when stored min/max look like absolute watts or BPM, not percent indices. */
export function storedTargetsLookLikeAbsolute(
  min: number,
  max: number,
  metric: PrimaryIntensityMetric,
): boolean {
  const mid = (min + max) / 2;
  if (metric === 'percentOfFtp') {
    return mid > ABSOLUTE_POWER_THRESHOLD || max > ABSOLUTE_POWER_THRESHOLD;
  }
  if (metric === 'percentOfLTHR' || metric === 'percentOfMaxHr') {
    return mid > HR_PERCENT_MAX || max > HR_PERCENT_MAX;
  }
  return false;
}

/** Setup target type «مطلق» — single absolute value, no zone / percent range UI. */
export function isAbsoluteTargetMode(
  targetOrRange: PrimaryIntensityTargetOrRange | string | undefined,
): boolean {
  return targetOrRange === 'single';
}

export const ABSOLUTE_UNIT_LABELS: Record<PrimaryIntensityMetric, string> = {
  percentOfFtp: 'W',
  percentOfMaxHr: 'bpm',
  percentOfLTHR: 'bpm',
  percentOfThresholdSpeed: '/km',
};

function formatPaceSecPerKm(totalSec: number): string {
  const sec = Math.max(0, Math.round(totalSec));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, '0')} /km`;
}

/** Upper bound for absolute-value intensity wheels. */
export function absoluteTargetMax(
  metric: PrimaryIntensityMetric,
  thresholds: IntensityThresholds,
): number {
  switch (metric) {
    case 'percentOfFtp':
      if (thresholds.ftp && thresholds.ftp > 0) {
        return Math.max(500, Math.round((FTP_PERCENT_MAX / 100) * thresholds.ftp));
      }
      return 2000;
    case 'percentOfMaxHr':
      return thresholds.maxHr && thresholds.maxHr > 0 ? thresholds.maxHr : 220;
    case 'percentOfLTHR':
      return thresholds.ltHr && thresholds.ltHr > 0
        ? Math.min(220, Math.round(thresholds.ltHr * 1.25))
        : 220;
    case 'percentOfThresholdSpeed':
      return 900;
    default:
      return 2000;
  }
}

export function formatAbsoluteTargetValue(
  value: number,
  metric: PrimaryIntensityMetric,
): string {
  const n = Math.round(value);
  switch (metric) {
    case 'percentOfFtp':
      return `${n} W`;
    case 'percentOfMaxHr':
    case 'percentOfLTHR':
      return `${n} bpm`;
    case 'percentOfThresholdSpeed':
      return formatPaceSecPerKm(n);
    default:
      return String(n);
  }
}

export function buildAbsoluteTargetOptions(
  metric: PrimaryIntensityMetric,
  thresholds: IntensityThresholds,
): string[] {
  const max = absoluteTargetMax(metric, thresholds);
  return [...Array(max + 1).keys()].map(i => formatAbsoluteTargetValue(i, metric));
}

/** Convert absolute UI value (W, bpm, sec/km) → percent index for API/Garmin storage. */
export function percentFromAbsoluteIndex(
  absValue: number,
  metric: PrimaryIntensityMetric,
  thresholds: IntensityThresholds,
): number {
  const abs = Math.round(absValue);
  switch (metric) {
    case 'percentOfFtp':
      return thresholds.ftp && thresholds.ftp > 0
        ? Math.min(FTP_PERCENT_MAX, Math.max(0, Math.round((abs / thresholds.ftp) * 100)))
        : abs;
    case 'percentOfLTHR':
      return thresholds.ltHr && thresholds.ltHr > 0
        ? Math.min(HR_PERCENT_MAX, Math.max(0, Math.round((abs / thresholds.ltHr) * 100)))
        : abs;
    case 'percentOfMaxHr':
      return thresholds.maxHr && thresholds.maxHr > 0
        ? Math.min(HR_PERCENT_MAX, Math.max(0, Math.round((abs / thresholds.maxHr) * 100)))
        : abs;
    case 'percentOfThresholdSpeed':
      if (!thresholds.thresholdSpeedMps || thresholds.thresholdSpeedMps <= 0 || abs <= 0) {
        return 0;
      }
      return Math.min(
        100,
        Math.max(0, Math.round(((1000 / abs) / thresholds.thresholdSpeedMps) * 100)),
      );
    default:
      return abs;
  }
}

/** Resolve wheel index from stored **percent** (single UI shows absolute equivalent). */
export function absoluteIndexFromStored(
  stored: number | undefined,
  metric: PrimaryIntensityMetric,
  thresholds: IntensityThresholds,
): number {
  if (stored == null || !Number.isFinite(Number(stored))) return 0;
  let n = Number(stored);
  const max = absoluteTargetMax(metric, thresholds);

  if (metric === 'percentOfFtp' && thresholds.ftp && thresholds.ftp > 0 && n <= FTP_PERCENT_MAX) {
    n = Math.round((n / 100) * thresholds.ftp);
  } else if (metric === 'percentOfLTHR' && thresholds.ltHr && thresholds.ltHr > 0 && n <= HR_PERCENT_MAX) {
    n = Math.round((n / 100) * thresholds.ltHr);
  } else if (metric === 'percentOfMaxHr' && thresholds.maxHr && thresholds.maxHr > 0 && n <= HR_PERCENT_MAX) {
    n = Math.round((n / 100) * thresholds.maxHr);
  } else if (
    metric === 'percentOfThresholdSpeed' &&
    thresholds.thresholdSpeedMps &&
    thresholds.thresholdSpeedMps > 0 &&
    n <= 100
  ) {
    n = Math.round(1000 / ((n / 100) * thresholds.thresholdSpeedMps));
  }

  return Math.min(Math.max(Math.round(n), 0), max);
}

/** List / step-config label — absolute only when target type is single (مطلق). */
export function formatBuilderIntensityLabel(
  minRaw: number | string | undefined,
  maxRaw: number | string | undefined,
  metric: PrimaryIntensityMetric,
  thresholds: IntensityThresholds,
  targetOrRange: PrimaryIntensityTargetOrRange | string | undefined,
): string {
  if (minRaw == null && maxRaw == null) return '—';
  if (isAbsoluteTargetMode(targetOrRange)) {
    const idx = absoluteIndexFromStored(
      Math.round((Number(minRaw ?? 0) + Number(maxRaw ?? minRaw ?? 0)) / 2),
      metric,
      thresholds,
    );
    return formatAbsoluteTargetValue(idx, metric);
  }
  return formatIntensityTargetLabel(minRaw, maxRaw, metric, thresholds);
}

export function buildLthrPercentOptions(ltHrValue: number): string[] {
  return [...Array(HR_PERCENT_MAX + 1).keys()].map(pct => {
    if (!ltHrValue) return `${pct}%`;
    const bpm = Math.round((pct * ltHrValue) / 100);
    return `${pct}% (${bpm} bpm)`;
  });
}

export function buildFtpPercentOptions(ftpValue: number): string[] {
  return [...Array(FTP_PERCENT_MAX + 1).keys()].map(
    item => `${item}% (${Math.round((item * ftpValue) / 100)} W)`,
  );
}

export function buildMaxHrPercentOptions(maxHrValue: number): string[] {
  return [...Array(HR_PERCENT_MAX + 1).keys()].map(pct => {
    if (!maxHrValue) return `${pct}%`;
    const bpm = Math.round((pct * maxHrValue) / 100);
    return `${pct}% (${bpm} bpm)`;
  });
}

export function buildPacePercentOptions(thresholdSpeedMps: number): string[] {
  return [...Array(101).keys()].map(pct => {
    if (!thresholdSpeedMps) return `${pct}%`;
    const speedMps = (pct / 100) * thresholdSpeedMps;
    if (speedMps <= 0) return `${pct}%`;
    return `${pct}% (${formatPaceSecPerKm(Math.round(1000 / speedMps))})`;
  });
}

/** Single-target (مطلق) wheel — percent index with absolute value in parentheses. */
export function buildSingleTargetPercentOptions(
  metric: PrimaryIntensityMetric,
  thresholds: IntensityThresholds,
): string[] {
  switch (metric) {
    case 'percentOfFtp':
      return buildFtpPercentOptions(thresholds.ftp ?? 0);
    case 'percentOfLTHR':
      return buildLthrPercentOptions(thresholds.ltHr ?? 0);
    case 'percentOfMaxHr':
      return buildMaxHrPercentOptions(thresholds.maxHr ?? 0);
    case 'percentOfThresholdSpeed':
      return buildPacePercentOptions(thresholds.thresholdSpeedMps ?? 0);
    default:
      return buildFtpPercentOptions(thresholds.ftp ?? 0);
  }
}

export function maxHrPercentIndexFromStored(
  stored: number | undefined,
  maxHrValue: number,
): number {
  if (stored == null) return 0;
  if (!maxHrValue) {
    return Math.min(Math.round(stored), HR_PERCENT_MAX);
  }
  const fromBpm = Math.floor((stored * 100) / maxHrValue);
  if (fromBpm > HR_PERCENT_MAX && stored <= HR_PERCENT_MAX) {
    return Math.round(stored);
  }
  return Math.min(Math.max(fromBpm, 0), HR_PERCENT_MAX);
}

/** Map stored percent → wheel index for single-target mode. */
export function singlePercentIndexFromStored(
  stored: number | undefined,
  metric: PrimaryIntensityMetric,
  thresholds: IntensityThresholds,
): number {
  switch (metric) {
    case 'percentOfFtp':
      return ftpPercentIndexFromStored(stored, thresholds.ftp ?? 0);
    case 'percentOfLTHR':
      return hrPercentIndexFromStored(stored, thresholds.ltHr ?? 0);
    case 'percentOfMaxHr':
      return maxHrPercentIndexFromStored(stored, thresholds.maxHr ?? 0);
    case 'percentOfThresholdSpeed':
      return Math.min(Math.max(Math.round(Number(stored) || 0), 0), 100);
    default:
      return 0;
  }
}

/** Map stored target → wheel index for % FTP (0…1000); handles legacy absolute watts. */
export function ftpPercentIndexFromStored(
  stored: number | undefined,
  ftpValue: number,
): number {
  if (stored == null || !Number.isFinite(Number(stored))) return 0;
  const n = Number(stored);
  if (n <= FTP_PERCENT_MAX) {
    return Math.min(Math.max(Math.round(n), 0), FTP_PERCENT_MAX);
  }
  if (ftpValue > 0) {
    return Math.min(Math.max(Math.round((n / ftpValue) * 100), 0), FTP_PERCENT_MAX);
  }
  return Math.min(Math.max(Math.round(n), 0), FTP_PERCENT_MAX);
}

export function hrPercentIndexFromStored(
  stored: number | undefined,
  ltHrValue: number,
): number {
  if (stored == null) return 0;
  if (!ltHrValue) {
    return Math.min(Math.round(stored), HR_PERCENT_MAX);
  }
  const fromBpm = Math.floor((stored * 100) / ltHrValue);
  if (fromBpm > HR_PERCENT_MAX && stored <= HR_PERCENT_MAX) {
    return Math.round(stored);
  }
  return Math.min(Math.max(fromBpm, 0), HR_PERCENT_MAX);
}

export function targetToIntensityPercent(
  target: { minValue?: number; maxValue?: number } | undefined,
  metric: PrimaryIntensityMetric | undefined,
  thresholds: { ftp?: number; ltHr?: number },
): number {
  if (!target) return 0;
  const resolvedMetric = metric ?? 'percentOfFtp';
  const { min, max } = normalizeStoredTargetToPercent(
    target.minValue,
    target.maxValue,
    resolvedMetric,
    thresholds,
  );
  const avg = min > 0 || max > 0 ? (min + max) / 2 : 0;
  if (!avg) return 0;
  return avg;
}

export function getCategoryFromPrimaryMetric(
  metric: PrimaryIntensityMetric,
): 'power' | 'hr' | 'speed' | null {
  if (metric === 'percentOfFtp') return 'power';
  if (metric === 'percentOfMaxHr' || metric === 'percentOfLTHR') return 'hr';
  if (metric === 'percentOfThresholdSpeed') return 'speed';
  return null;
}

export function getZonesFromSettings(
  zoneSettings: Record<string, unknown> | null | undefined,
  category: 'power' | 'hr' | 'speed',
): { label?: string; minimum?: number; maximum?: number }[] {
  const key =
    category === 'power'
      ? 'power_zones'
      : category === 'hr'
        ? 'heart_rate_zones'
        : 'speed_zones';
  const block = zoneSettings?.[key] as { zones?: unknown[]; range_percent?: unknown[] } | undefined;
  return (block?.zones ?? block?.range_percent ?? []) as {
    label?: string;
    minimum?: number;
    maximum?: number;
  }[];
}

/** API `zones` are absolute W/bpm/m/s; legacy `range_percent` is already percent.
 * Only bounded ranges can be applied to a workout without inventing an upper limit.
 */
export function getBuilderZoneRanges(
  settings: Record<string, unknown> | null | undefined,
  metric: PrimaryIntensityMetric,
  thresholds: IntensityThresholds,
): { label: string; number: number; minimum: number; maximum: number }[] {
  const category = getCategoryFromPrimaryMetric(metric);
  const key = category === 'power' ? 'power_zones' : category === 'hr' ? 'heart_rate_zones' : 'speed_zones';
  const block = settings?.[key] as { zones?: unknown[]; range_percent?: unknown[] } | undefined;
  const absolute = Array.isArray(block?.zones);
  const rows = absolute ? block!.zones : block?.range_percent;
  const threshold = metric === 'percentOfFtp' ? thresholds.ftp
    : metric === 'percentOfMaxHr' ? thresholds.maxHr
    : metric === 'percentOfLTHR' ? thresholds.ltHr : thresholds.thresholdSpeedMps;
  if (!category || !Array.isArray(rows) || (absolute && (!threshold || !Number.isFinite(threshold) || threshold <= 0))) return [];
  return rows.flatMap((row, index) => {
    if (!row || typeof row !== 'object') return [];
    const zone = row as { label?: string; minimum?: number | string; maximum?: number | string };
    if (zone.minimum == null || zone.maximum == null || zone.minimum === '' || zone.maximum === '') return [];
    const min = Number(zone.minimum);
    const max = Number(zone.maximum);
    if (!Number.isFinite(min) || !Number.isFinite(max) || min < 0 || max < min) return [];
    const scale = absolute ? 100 / threshold! : 1;
    const limit = category === 'hr' ? HR_PERCENT_MAX : FTP_PERCENT_MAX;
    if (max * scale > limit) return [];
    return [{ label: zone.label || `Z${index + 1}`, number: trainingZoneNumber(zone.label ?? '') ?? index + 1, minimum: Number((min * scale).toFixed(6)), maximum: Number((max * scale).toFixed(6)) }];
  });
}

/** Walk blocks and normalize step targets before API persistence (always percent). */
export function normalizeWorkoutBlocksTargets(
  blocks: unknown[],
  metric: PrimaryIntensityMetric,
  thresholds: IntensityThresholds,
  targetOrRange?: PrimaryIntensityTargetOrRange | string,
): unknown[] {
  if (!Array.isArray(blocks)) return blocks;
  const singleMode = isAbsoluteTargetMode(targetOrRange);

  const normalizeNode = (node: Record<string, unknown>): Record<string, unknown> => {
    const out = { ...node };

    if (Array.isArray(out.targets) && (out.targets as unknown[])[0]) {
      const t = (out.targets as { minValue?: number; maxValue?: number }[])[0];
      const { min, max } = normalizeStoredTargetToPercent(
        t.minValue,
        t.maxValue,
        metric,
        thresholds,
      );
      const pctMin = singleMode ? Math.round((min + max) / 2) : min;
      const pctMax = singleMode ? pctMin : max;
      out.targets = [
        { ...t, minValue: pctMin, maxValue: pctMax },
        ...(out.targets as unknown[]).slice(1),
      ];
    }

    if (Array.isArray(out.steps)) {
      out.steps = (out.steps as Record<string, unknown>[]).map(s => normalizeNode(s));
    }

    return out;
  };

  return (blocks as Record<string, unknown>[]).map(block => normalizeNode(block));
}
